// libraries
import { useTranslation } from 'react-i18next';
import { MouseEventHandler, useCallback, useMemo, useState } from 'react';

// components
import { ContextMenu } from 'components/mrp/components/ContextMenu';
import { Tooltip, useTheme } from '@mui/material';
import { EntityInfoTable } from 'components/shared/EntityInfoTable';

// styles
import {
  ActionsContainer,
  CalendarHeaderButtonIcon,
  Container,
  ContentContainer,
  HeaderContentContainer,
  LabelContainer,
  ProgressBarContainer,
  SlimLinearProgress,
  EntitySubLabel
} from './CalendarHeader.styles';

// types
import { CalendarHeaderProps } from './CalendarHeader.types';
import { ContextMenuAction } from 'components/mrp/components/ContextMenu/ContextMenu.types';
import { EntityInfoRow } from 'components/shared/EntityInfoTable';
import { ClientResponse, EmployeeInPeriodResponse } from 'generated/index';

// stores
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useShallow } from 'zustand/shallow';

// utils
import {
  formatEntityDisplayLabel,
  getEntityCounters,
  getEntityIcon,
  getUniqueEntities
} from 'components/mrp/mrp.utils';
import { formatDate } from 'utils/date';

// constants
import {
  ENTITY_TYPES_WITH_HOVER_TOOLTIP,
  EntityType
} from 'components/mrp/mrp.consts';
import { useEntityListener } from 'components/mrp/mrp.hooks';
import { EntityPickerLabel } from '../EntityPickerLabel';
import {
  formatTreatments,
  getCloseActions,
  getFormattedTreatments,
  getMergeActions,
  getMoveActions,
  getNavigateToAppointmentsOverviewAction,
  getSplitActions
} from './CalendarHeader.utils';

const CalendarHeader = ({ displayData }: CalendarHeaderProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });
  const theme = useTheme();

  const [isEntityPickerOpen, setIsEntityPickerOpen] = useState<boolean>(false);

  const {
    calendars,
    closeCalendar,
    toggleCalendarPin,
    mergeCalendars,
    moveCalendar,
    splitCalendar
  } = useCalendarStore(
    useShallow((state) => ({
      calendars: state.calendars,
      closeCalendar: state.control.closeCalendar,
      toggleCalendarPin: state.control.toggleCalendarPin,
      mergeCalendars: state.control.mergeCalendars,
      moveCalendar: state.control.moveCalendar,
      splitCalendar: state.control.splitCalendar
    }))
  );

  const planningEntityIdentifiers = useMemo(
    () =>
      displayData.sources.flatMap(
        ({ planningEntityIdentifier }) => planningEntityIdentifier
      ),
    [displayData.sources]
  );

  const { isLoading, planningEntities } = useEntityListener({
    calendarId: displayData.calendarId,
    planningEntityIdentifiers,
    listen: false
  });

  const uniqueEntities = useMemo(() => {
    return getUniqueEntities(displayData.sources);
  }, [displayData.sources]);

  const planningEntityForHeader =
    uniqueEntities.length === 1 ? planningEntities[0] ?? null : null;

  const label = useMemo(() => {
    if (uniqueEntities.length === 0) return '';
    if (uniqueEntities.length > 1) {
      return (
        <>
          {t('mergedCalendar')}
          {getEntityCounters(
            uniqueEntities.map(
              (x) => x.planningEntityIdentifier.planningEntityType
            )
          )}
        </>
      );
    }

    const { label: entityLabel, planningEntityIdentifier } = uniqueEntities[0];
    return formatEntityDisplayLabel(
      entityLabel,
      planningEntityIdentifier.number
    );
  }, [uniqueEntities, t]);

  const iconEntityType = useMemo(
    () =>
      uniqueEntities.length > 1
        ? 'MIX'
        : uniqueEntities[0]?.planningEntityIdentifier.planningEntityType,
    [uniqueEntities]
  );

  const icon = useMemo(
    () => getEntityIcon(iconEntityType, undefined, 'LARGE', true),
    [iconEntityType]
  );

  const entityInfo = useMemo(() => {
    if (!planningEntityForHeader) return null;

    const planningEntityIdentifier = uniqueEntities[0].planningEntityIdentifier;

    if (
      !ENTITY_TYPES_WITH_HOVER_TOOLTIP.includes(
        planningEntityIdentifier.planningEntityType
      )
    ) {
      return null;
    }

    const clientEntity = planningEntityForHeader.planningEntity as
      | ClientResponse
      | undefined;

    return {
      displayName:
        clientEntity?.displayName || planningEntityForHeader.name || '',
      dateOfBirth: clientEntity?.dateOfBirth,
      number: clientEntity?.number,
      treatments: clientEntity?.treatments
    };
  }, [uniqueEntities, planningEntityForHeader]);

  const splitActions = useMemo(
    () =>
      uniqueEntities.length > 1
        ? [
            getSplitActions({
              splitCalendar,
              displayData
            })
          ]
        : [],
    [uniqueEntities.length, splitCalendar, displayData]
  );

  const moveActions = useMemo(
    () =>
      uniqueEntities.length === 1
        ? [
            {
              value: t('move'),
              subActions: getMoveActions({
                calendars,
                displayData,
                moveCalendar
              })
            } as ContextMenuAction
          ]
        : [],
    [uniqueEntities.length, t, calendars, displayData, moveCalendar]
  );

  const navigateToAppointmentsOverviewActions = useMemo(() => {
    const action = getNavigateToAppointmentsOverviewAction({ displayData });
    return action ? [action] : [];
  }, [displayData]);

  const actions: ContextMenuAction[] = useMemo(
    () => [
      ...moveActions,
      getCloseActions({
        calendars,
        displayData,
        closeCalendar
      }),
      getMergeActions({
        mergeCalendars,
        displayData,
        calendars
      }),
      ...splitActions,
      ...navigateToAppointmentsOverviewActions
    ],
    [
      moveActions,
      calendars,
      displayData,
      closeCalendar,
      mergeCalendars,
      splitActions,
      navigateToAppointmentsOverviewActions
    ]
  );

  const middleClickClose: MouseEventHandler = useCallback(
    (ev) => {
      if (ev.button != 1) return;

      closeCalendar(displayData.calendarId, true);
      ev.preventDefault();
      ev.stopPropagation();
    },
    [closeCalendar, displayData.calendarId]
  );

  const handleEntityPickerOpen = useCallback(() => {
    setIsEntityPickerOpen(true);
  }, []);

  const handleTogglePin = useCallback(() => {
    toggleCalendarPin(displayData.calendarId);
  }, [toggleCalendarPin, displayData.calendarId]);

  const handleClose = useCallback(() => {
    closeCalendar(displayData.calendarId, true);
  }, [closeCalendar, displayData.calendarId]);

  const formattedTreatments = useMemo(
    () => getFormattedTreatments(entityInfo?.treatments || []),
    [entityInfo?.treatments]
  );

  const treatmentDisplayText = useMemo(() => {
    if (formattedTreatments.length === 0) return t('noTreatments');
    return formattedTreatments.map((row) => row.groupName).join(', ');
  }, [formattedTreatments, t]);

  const employeeFunctionDisplayText = useMemo(() => {
    if (!planningEntityForHeader) return null;
    const planningEntityIdentifier = uniqueEntities[0].planningEntityIdentifier;
    if (planningEntityIdentifier.planningEntityType !== EntityType.EMPLOYEE) {
      return null;
    }
    const employeeInPeriod = planningEntityForHeader.planningEntity as
      | EmployeeInPeriodResponse
      | undefined;

    const functions = employeeInPeriod?.activeFunctions ?? [];
    if (functions.length === 0) return t('noFunction');
    return functions
      .map((fn) => fn.description ?? fn.code ?? '')
      .filter(Boolean)
      .join(', ');
  }, [uniqueEntities, planningEntityForHeader, t]);

  const entityInfoRows: EntityInfoRow[] = useMemo(() => {
    if (!entityInfo) return [];

    return [
      {
        label: t('entityInfo.name'),
        value: entityInfo.displayName
      },
      {
        label: t('entityInfo.dateOfBirth'),
        value: formatDate(entityInfo.dateOfBirth),
        hidden: !entityInfo.dateOfBirth
      },
      {
        label: t('entityInfo.patientNumber'),
        value: entityInfo.number,
        hidden: !entityInfo.number
      },
      {
        label: t('entityInfo.treatments'),
        value: formatTreatments(formattedTreatments, theme.palette.divider),
        hidden: formattedTreatments.length === 0
      }
    ];
  }, [entityInfo, formattedTreatments, t, theme.palette.divider]);

  if (uniqueEntities.length === 0) return null;

  return (
    <Container onAuxClick={middleClickClose} onMouseDown={middleClickClose}>
      <HeaderContentContainer>
        <Tooltip
          arrow
          enterDelay={500}
          enterNextDelay={500}
          title={
            entityInfoRows.length > 0 ? (
              <EntityInfoTable rows={entityInfoRows} />
            ) : (
              ''
            )
          }
        >
          <span>{icon}</span>
        </Tooltip>

        <ContentContainer>
          <LabelContainer>
            <EntityPickerLabel
              label={label}
              calendarId={displayData.calendarId}
              isPopoverOpen={isEntityPickerOpen}
              setIsPopoverOpen={setIsEntityPickerOpen}
            />
          </LabelContainer>
          {iconEntityType === EntityType.CLIENT && (
            <EntitySubLabel>{treatmentDisplayText}</EntitySubLabel>
          )}
          {iconEntityType === EntityType.EMPLOYEE &&
            employeeFunctionDisplayText != null && (
              <EntitySubLabel>{employeeFunctionDisplayText}</EntitySubLabel>
            )}
        </ContentContainer>

        <ActionsContainer>
          <ContextMenu id="panel-header-context-menu" actions={actions}>
            <CalendarHeaderButtonIcon type="ellipsis-stroke-vertical" />
          </ContextMenu>

          <CalendarHeaderButtonIcon
            type="arrow-right-arrow-left"
            onClick={handleEntityPickerOpen}
          />

          <CalendarHeaderButtonIcon
            type={displayData.isPinned ? 'thumbtack-fill' : 'thumbtack'}
            onClick={handleTogglePin}
          />

          <CalendarHeaderButtonIcon type="xmark" onClick={handleClose} />
        </ActionsContainer>
      </HeaderContentContainer>
      <ProgressBarContainer isVisible={isLoading}>
        <SlimLinearProgress variant="indeterminate" />
      </ProgressBarContainer>
    </Container>
  );
};

export default CalendarHeader;
